import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { serialize } from 'object-to-formdata';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { AvatarAdapter } from '../../adapters/avatar.adapter';
import { urlToFile } from '../../functions/urlToFile';
import {
  deleteUserAvatar,
  deleteUserAvatarFailure,
  deleteUserAvatarSuccess,
  loadUserAvatar,
  loadUserAvatarFailure,
  loadUserAvatarSuccess,
  updateUserAvatar,
  updateUserAvatarFailure,
  updateUserAvatarSuccess,
} from './user-avatar.actions';

@Injectable()
export class UserAvatarEffects {
  private readonly actions$ = inject(Actions);
  private readonly avatarAdapter = inject(AvatarAdapter);
  private readonly toastsService = inject(ToastsService);
  
  loadUserAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserAvatar),
      exhaustMap(() =>
        this.avatarAdapter.getUserAvatar().pipe(
          map((result) => loadUserAvatarSuccess(result)),
          catchError((error) => {
            return of(loadUserAvatarFailure({ error }));
          })
        )
      )
    )
  );

  updateUserAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserAvatar),
      concatMap(async (props) => serialize({ image: await urlToFile(props.payload, 'image.png', 'image/png') })),
      mergeMap((data) =>
        this.avatarAdapter.editUserAvatar(data).pipe(
          map((result) => updateUserAvatarSuccess(result)),
          catchError((error) => {
            return of(updateUserAvatarFailure({ error }));
          })
        )
      )
    )
  );

  updateUserAvatarSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserAvatarSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.profile.account.toasts.image-update-success');
        })
      ),
    { dispatch: false }
  );

  deleteUserAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteUserAvatar),
      mergeMap((props) =>
        this.avatarAdapter.deleteUserAvatar().pipe(
          map(() => deleteUserAvatarSuccess()),
          catchError((error) => {
            return of(deleteUserAvatarFailure({ error }));
          })
        )
      )
    )
  );

  deleteUserAvatarSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteUserAvatarSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.profile.account.toasts.image-delete-success');
        })
      ),
    { dispatch: false }
  );

  loadUserAvatarFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserAvatarFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );
}
